import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ErrorBoundary } from './components/shared/ErrorBoundary';
import { Toaster } from './components/ui/sonner';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AssetRegistryPage from './pages/AssetRegistryPage';
import AssetDetailPage from './pages/AssetDetailPage';
import WorkOrdersPage from './pages/WorkOrdersPage';
import AlertsPage from './pages/AlertsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SettingsPage from './pages/SettingsPage';
import SparePartsPage from './pages/SparePartsPage';
import ComponentsPage from './pages/ComponentsPage';
import ComponentFormPage from './pages/ComponentFormPage';
import ComponentDetailPage from './pages/ComponentDetailPage';
import SiteOperationsPage from './pages/SiteOperationsPage';
import SiteVisitDetailPage from './pages/SiteVisitDetailPage';
import ActionPlansPage from './pages/ActionPlansPage';
import ActionPlanDetailPage from './pages/ActionPlanDetailPage';
// Admin Pages
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import UserManagementPage from './pages/admin/UserManagementPage';
import OrganizationSettingsPage from './pages/admin/OrganizationSettingsPage';
import VisitTemplatesPage from './pages/admin/VisitTemplatesPage';
import PortfolioRecordsPage from './pages/admin/PortfolioRecordsPage';
import CompaniesPage from './pages/admin/CompaniesPage';
import DataIntegrationsPage from './pages/admin/DataIntegrationsPage';
// Commercial & Finance
import CommercialPage from './pages/CommercialPage';
import FinancePage from './pages/FinancePage';
// SSO
import SSOCallbackPage from './pages/SSOCallbackPage';
// User Activity Analytics
import UserActivityAnalyticsPage from './pages/UserActivityAnalyticsPage';
import './App.css';

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <ErrorBoundary>{children}</ErrorBoundary>;
}

// Public Route wrapper (redirects to dashboard if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      
      {/* SSO Callback - no auth check needed */}
      <Route path="/sso-callback" element={<SSOCallbackPage />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets"
        element={
          <ProtectedRoute>
            <AssetRegistryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/assets/:id"
        element={
          <ProtectedRoute>
            <AssetDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/work-orders"
        element={
          <ProtectedRoute>
            <WorkOrdersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute>
            <AlertsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/spare-parts"
        element={
          <ProtectedRoute>
            <SparePartsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/components"
        element={
          <ProtectedRoute>
            <ComponentsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/components/new"
        element={
          <ProtectedRoute>
            <ComponentFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/components/:id"
        element={
          <ProtectedRoute>
            <ComponentDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/components/:id/edit"
        element={
          <ProtectedRoute>
            <ComponentFormPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/site-operations"
        element={
          <ProtectedRoute>
            <SiteOperationsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/site-operations/:id"
        element={
          <ProtectedRoute>
            <SiteVisitDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/action-plans"
        element={
          <ProtectedRoute>
            <ActionPlansPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/action-plans/:id"
        element={
          <ProtectedRoute>
            <ActionPlanDetailPage />
          </ProtectedRoute>
        }
      />

      {/* Commercial & Finance Routes */}
      <Route
        path="/commercial"
        element={
          <ProtectedRoute>
            <CommercialPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/finance"
        element={
          <ProtectedRoute>
            <FinancePage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <ProtectedRoute>
            <UserManagementPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/organizations"
        element={
          <ProtectedRoute>
            <SuperAdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/organizations/:id"
        element={
          <ProtectedRoute>
            <OrganizationSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/org-settings"
        element={
          <ProtectedRoute>
            <OrganizationSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/user-analytics"
        element={
          <ProtectedRoute>
            <UserActivityAnalyticsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/visit-templates"
        element={
          <ProtectedRoute>
            <VisitTemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/portfolios"
        element={
          <ProtectedRoute>
            <PortfolioRecordsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/companies"
        element={
          <ProtectedRoute>
            <CompaniesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/integrations"
        element={
          <ProtectedRoute>
            <DataIntegrationsPage />
          </ProtectedRoute>
        }
      />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster richColors position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
